import React, { useEffect, useState } from 'react';
import axios from 'axios';

/* Change audit (2026-10-24) — the Wallboard of TEC sub-admins: every change made by
   TEC administrators and the Operations team in seven modules, with before → after. */
const MOD_COLORS: Record<string, string> = {
  'General Lines': '#2563eb', 'User Extensions': '#7c3aed', 'Receptionist Group': '#0891b2', 'Unavailable Greetings': '#db2777',
  'Call Forwarding': '#ea580c', 'Voicemail': '#16a34a', 'Billing': '#b45309',
};
const box: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, marginBottom: 14 };
const dubaiToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai' }).format(new Date());
const fmt = (d: string) => new Date(d).toLocaleString('en-GB', { timeZone: 'Asia/Dubai', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
const err = (e: any) => e?.response?.data?.error || e?.message || 'Something went wrong';

function Changes({ list }: { list: any[] }) {
  if (!list || !list.length) return <span style={{ color: '#9ca3af' }}>—</span>;
  return (
    <div style={{ display: 'grid', gap: 3 }}>{list.map((c: any, i: number) => (
      <div key={i} style={{ fontSize: 12, lineHeight: 1.35 }}>
        <span style={{ color: '#6b7280' }}>{c.f}: </span>
        {c.b !== '' && <span style={{ background: '#fef2f2', color: '#991b1b', borderRadius: 4, padding: '0 4px', textDecoration: c.a !== '' ? 'line-through' : 'none' }}>{String(c.b)}</span>}
        {c.b !== '' && c.a !== '' && <span style={{ color: '#9ca3af' }}> → </span>}
        {c.a !== '' && <span style={{ background: '#f0fdf4', color: '#166534', borderRadius: 4, padding: '0 4px' }}>{String(c.a)}</span>}
        {c.b === '' && c.a === '' && <span style={{ color: '#9ca3af' }}>(cleared)</span>}
      </div>))}</div>
  );
}

export default function ChangeAuditBoard() {
  const [from, setFrom] = useState(dubaiToday());
  const [to, setTo] = useState(dubaiToday());
  const [mod, setMod] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState<any>(null);
  const [msg, setMsg] = useState('');
  const [at, setAt] = useState<Date | null>(null);
  useEffect(() => {
    let live = true;
    const load = () => axios.get('/api/change-audit', { params: { from, to } })
      .then((r) => { if (live) { setData(r.data); setMsg(''); setAt(new Date()); } })
      .catch((e) => { if (live) setMsg(err(e)); });
    load();
    const t = setInterval(load, 30000);
    return () => { live = false; clearInterval(t); };
  }, [from, to]);

  const ql = q.trim().toLowerCase();
  const events = (data?.events || []).filter((e: any) => (!mod || e.module === mod)
    && (!ql || [e.who, e.username, e.item, e.client, e.action, JSON.stringify(e.changes || [])].join(' ').toLowerCase().includes(ql)));
  const total = (data?.modules || []).reduce((s: number, m: any) => s + m.count, 0);
  const tile = (key: string, label: string, n: number, color: string) => (
    <button key={key} onClick={() => setMod(mod === key ? '' : key)}
      style={{ textAlign: 'left', border: `1px solid ${mod === key ? color : '#e5e7eb'}`, background: mod === key ? color + '12' : '#fff', borderRadius: 10, padding: '10px 12px', cursor: 'pointer', borderTop: `4px solid ${color}` }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>{n}</div>
      <div style={{ fontSize: 12, color: '#475569' }}>{label}</div>
    </button>);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
        <div>
          <h2 style={{ margin: '0 0 4px' }}>Change audit</h2>
          <div style={{ color: '#6b7280', fontSize: 13 }}>Every change made by administrators and the Operations team: who, when, what it was before and what it is now. Updates every 30 seconds.</div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12 }}>From<br /><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
          <label style={{ fontSize: 12 }}>To<br /><input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
          <label style={{ fontSize: 12 }}>Search<br /><input placeholder="Client, extension, line, person…" value={q} onChange={(e) => setQ(e.target.value)} style={{ minWidth: 220 }} /></label>
        </div>
      </div>
      {msg && <div style={{ ...box, color: '#b91c1c' }}>{msg}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
        {tile('', 'All changes', total, '#0f172a')}
        {(data?.modules || []).map((m: any) => tile(m.module, m.module, m.count, MOD_COLORS[m.module] || '#64748b'))}
      </div>
      <div style={box}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13 }}>
          <b>{mod || 'All modules'} — {events.length} change{events.length === 1 ? '' : 's'}</b>
          {at && <span style={{ color: '#9ca3af', fontSize: 12 }}>Updated {at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>}
        </div>
        {!data ? <div style={{ color: '#6b7280' }}>Loading…</div> : !events.length ? <div style={{ color: '#6b7280', fontSize: 13 }}>No changes in this period.</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                {['When', 'Who', 'Module', 'Client', 'Item', 'Action', 'Before → after'].map((h) => <th key={h} style={{ padding: '7px 8px', whiteSpace: 'nowrap' }}>{h}</th>)}
              </tr></thead>
              <tbody>{events.map((e: any) => (
                <tr key={e.id} style={{ borderTop: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                  <td style={{ padding: '7px 8px', whiteSpace: 'nowrap', color: '#475569' }}>{fmt(e.at)}</td>
                  <td style={{ padding: '7px 8px' }}><b>{e.who || '—'}</b>{e.actor_role && <div style={{ fontSize: 11, color: '#6b7280' }}>{e.actor_role === 'tenant_admin' ? 'Administrator' : e.actor_role === 'pbx_admin' ? 'Sub-admin' : `${e.actor_role} group`}</div>}</td>
                  <td style={{ padding: '7px 8px', whiteSpace: 'nowrap' }}><span style={{ color: MOD_COLORS[e.module] || '#334155', fontWeight: 600 }}>{e.module}</span></td>
                  <td style={{ padding: '7px 8px' }}>{e.client || '—'}</td>
                  <td style={{ padding: '7px 8px' }}>{e.item}</td>
                  <td style={{ padding: '7px 8px' }}>{e.action}{e.currency && e.module === 'Billing' ? <div style={{ fontSize: 11, color: '#6b7280' }}>Currency {e.currency}</div> : null}</td>
                  <td style={{ padding: '7px 8px', minWidth: 260 }}><Changes list={e.changes} /></td>
                </tr>))}</tbody>
            </table>
          </div>)}
      </div>
    </div>
  );
}

// Wallboard page for sub-admins of a customer tenant (TEC): the change audit only.
// Everyone else — and sub-admins of other tenants — keep the normal wallboard.
export function SubAdminWallboard({ fallback }: { fallback: React.ReactNode }) {
  const [on, setOn] = useState<boolean | null>(null);
  useEffect(() => { axios.get('/api/change-audit/available').then((r) => setOn(!!r.data?.on)).catch(() => setOn(false)); }, []);
  if (on === null) return null;
  return on ? <ChangeAuditBoard /> : <>{fallback}</>;
}
